import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { CSSPlugin } from "gsap/CSSPlugin";
import "../styles/components/EditTransition.scss";
import { useLanguage } from '../contexts/LanguageContext';
import ActivityGainToast from "./ActivityGainToast";

// Force CSSPlugin to not get dropped during build
gsap.registerPlugin(CSSPlugin);

const EditTransition = ({ isSaveClicked, activityGains = [] }) => {
  const { t } = useLanguage();
  const componentRef = useRef(null);
  const revealRef = useRef(null);
  const svgRef = useRef(null);

  const [activeGainIndex, setActiveGainIndex] = useState(0);

  const safeActivityGains = Array.isArray(activityGains) ? activityGains : [];
  const activeActivityGain = safeActivityGains[activeGainIndex] || null;

  useEffect(() => {
    if (!isSaveClicked) return;

    setActiveGainIndex(0);
  }, [isSaveClicked, safeActivityGains.length]);

  const handleActivityGainDone = () => {
    setActiveGainIndex((currentIndex) => currentIndex + 1);
  };

  useEffect(() => {
    if (!isSaveClicked) return;

    const isMobile = window.innerWidth <= 768;
    const targetScale = isMobile ? 0.9 : 1.2;

    const ctx = gsap.context(() => {
      const line1Chars = ".line1 .char";
      const line2Chars = ".line2 .char";
      const arrow = svgRef.current.querySelectorAll("#_Arrow_ .cls-1");

      const timelinevar = gsap.timeline({ defaults: { ease: "power4.out", duration: 0.7 } });

      // Centre la flèche horizontalement et la décale vers le bas pour un meilleur alignement
      gsap.set(svgRef.current.querySelector("#_Arrow_"), { autoAlpha: 1, x: (1920 - 424) / 2, y: 100 });
      gsap.set(revealRef.current, { scaleY: 1, transformOrigin: "bottom" });

      timelinevar.from(line1Chars, {
        yPercent: -200,
        stagger: 0.03,
        skewY: 60,
        skewX: 30,
        scaleY: 0.9,
        opacity: 0,
      });
      timelinevar.from(line2Chars, {
        yPercent: 215,
        stagger: 0.03,
        skewY: 60,
        skewX: 30,
        scaleY: 0.5,
        opacity: 0,
      }, "-=0.7");
      timelinevar.to(line2Chars, {
        xPercent: 0,
        ease: "elastic.out(1, 0.5)",
      }, "1.3");
      timelinevar.from(arrow, {
        opacity: 0,
        ease: "elastic.out(1, 0.5)",
      }, "-=0.7");
      timelinevar.to(line1Chars, {
        stagger: 0.03,
        scaleX: 0,
        skewX: 30,
        x: 30,
        opacity: 0,
        duration: 0.2,
      }, "+=1");
      timelinevar.to(line2Chars, {
        stagger: 0.03,
        scaleX: 0,
        skewX: 30,
        x: 30,
        opacity: 0,
        duration: 0.2,
      }, "<");
      timelinevar.to(arrow, {
        xPercent: 200,
        duration: 1.3,
        ease: "elastic.out(1, 0.3)",
      }, "<");
      timelinevar.to(arrow, {
        rotateZ: -90,
        transformOrigin: "center center",
      }, "-=0.1");
      timelinevar.to(arrow, {
        yPercent: 1000,
        duration: 1.3,
      }, "-=0.5");
      timelinevar.to(arrow, {
        yPercent: -2690,
        ease: "back.in(1)",
        duration: 0.5,
      }, "-=0.3");
      timelinevar.to(revealRef.current, {
        scaleY: 0,
        transformOrigin: "top",
        duration: 1.2,
        ease: "power4.inOut",
      }, "-=0.6");

    }, componentRef);

    return () => ctx.revert();
  }, [isSaveClicked]);

  const splitText = (text) => {
    return text.split('').map((char, index) => <span key={index} className="char">{char === ' ' ? '\u00A0' : char}</span>);
  };

  return (
    <div className="editTransition" ref={componentRef}>
      {activeActivityGain && (
        <ActivityGainToast
          visible={Boolean(activeActivityGain)}
          toastId={activeActivityGain.id}
          points={activeActivityGain.points}
          unitLabel={t("profilMember.activity")}
          label={
            activeActivityGain.labelKey
              ? t(activeActivityGain.labelKey)
              : activeActivityGain.label || ""
          }
          variant={activeActivityGain.variant || "profile"}
          className="activity-gain-toast--profile-transition"
          duration={2300}
          onDone={handleActivityGainDone}
        />
      )}
      <div className="transition-text-container">
        <h1 className="line1">{splitText(t('editProfile.editTransition.line1'))}</h1>
        <h1 className="line2">{splitText(t('editProfile.editTransition.line2'))}</h1>
      </div>
      <svg
        ref={svgRef}
        x="0px"
        y="0px"
        viewBox="0 0 1920 1080"
        style={{
          width: "100%",
          height: "100%",
          position: "absolute",
          top: "0",
          left: "0",
          zIndex: "9999",
          overflow: "hidden",
          pointerEvents: "none",
          fill: "#D90093",
        }}
      >
        <g id="_Arrow_">
          <polygon className="cls-1" points="376.49 727.13 329.16 703.86 329.16 732.33 105.2 732.33 105.2 776.49 329.16 776.49 329.16 802.23 376.49 778.96 423.81 750.39 376.49 727.13" />
        </g>
      </svg>
      <div className="reveal" ref={revealRef}></div>
    </div>
  );
};

export default EditTransition;
